import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, input, Optional, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { A11yModule } from '@angular/cdk/a11y';
import { connectState } from '@examdojo/angular/util';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ContentChange, QuillEditorComponent, QuillModules } from 'ngx-quill';
import Quill from 'quill';
import { BehaviorSubject, debounceTime, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuTrigger, MatMenu, MatMenuContent } from '@angular/material/menu';
import { ButtonComponent } from '@examdojo/core/button';
import { ImageChatMessageContentStoreModel, ScrollTriggerProvider } from '../../models';

@UntilDestroy()
@Component({
  selector: 'dojo-chat-input',
  standalone: true,
  imports: [
    QuillEditorComponent,
    ReactiveFormsModule,
    IconButtonComponent,
    NgClass,
    ButtonComponent,
    MatMenuTrigger,
    MatMenu,
    MatMenuContent,
    A11yModule,
  ],
  templateUrl: './chat-input.component.html',
  styleUrls: ['./chat-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatInputComponent {
  constructor(
    @Optional() private readonly scrollTriggerProvider: ScrollTriggerProvider,
    private readonly destroyRef: DestroyRef,
  ) {}

  readonly inputDisabled = input<boolean>(false);
  readonly isChatCreated = input<boolean>(false);
  readonly submitMessage = output<ImageChatMessageContentStoreModel>();
  readonly restartChat = output<void>();

  private readonly editorContent$$ = new BehaviorSubject<ContentChange | undefined>(undefined);

  readonly messageForm = new FormGroup<{ message: FormControl<string> }>({
    message: new FormControl<string>('', { nonNullable: true }),
  });

  readonly isNonEmpty = signal(false);

  readonly quillModules: QuillModules = {
    toolbar: false,
    keyboard: {
      bindings: {
        Enter: {
          key: 'Enter',
          handler: () => {
            this.submit();
          },
        },
        EnterAndShort: {
          key: 'Enter',
          shortKey: true,
          handler: () => {
            this.submit();
          },
        },
      },
    },
  };

  readonly state = connectState({});

  editorCreated(editorInstance: Quill) {
    this.scrollTriggerProvider?.scrollTrigger$
      .pipe(
        debounceTime(100),
        tap(() => {
          editorInstance.focus();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  contentChanged(content: ContentChange) {
    this.editorContent$$.next(content);
    const editorContent = this.getMessageContent(content);
    this.isNonEmpty.set(editorContent.trim().length > 0);
  }

  submit() {
    if (this.inputDisabled()) {
      return;
    }

    const editorContent = this.editorContent$$.value;
    if (!editorContent) {
      return;
    }

    const messageText = this.getMessageContent(editorContent).trim();
    if (!messageText.length) {
      return;
    }

    this.submitMessage.emit({
      images: [],
      text: messageText,
    });

    this.messageForm.reset();
  }

  private getMessageContent(contentChange: ContentChange): string {
    const editorContent = contentChange.content;
    let message = '';

    (editorContent?.['ops'] || []).forEach((chunk: { insert?: string | Record<string, unknown> }) => {
      if (typeof chunk.insert === 'string') {
        message += chunk.insert;
      }
    });

    return message;
  }
}
